import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  FiTrendingUp, FiTrendingDown, FiDollarSign, FiPercent,
  FiSearch, FiDownload, FiPrinter, FiChevronDown, FiChevronRight,
  FiCalendar, FiEye, FiEyeOff, FiBarChart2
} from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { formatNumber } from '../../utils/formatters';
import useCurrency from '../../hooks/useCurrency';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { recordReads } from '../../utils/readTelemetry';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line,
  AreaChart, Area
} from 'recharts';
import { addPrintBrand } from '../../utils/printBranding';

// Chart colors
const CHART_COLORS = {
  primary: '#800020',
  secondary: '#A0153E',
  tertiary: '#5D0017',
  accent1: '#C4A484',
  accent2: '#8B7355',
  accent3: '#D4AF37'
};

const PIE_COLORS = [
  CHART_COLORS.primary,
  CHART_COLORS.secondary,
  CHART_COLORS.accent1,
  CHART_COLORS.accent2,
  CHART_COLORS.accent3,
  CHART_COLORS.tertiary,
  '#6A994E',
  '#BC6C25',
  '#606C38',
  '#DDA15E'
];

// Local format helpers for print/export (independent of imported formatters)
const fmtAmount = (amount) => {
  const dp = (typeof window !== 'undefined' && Number.isFinite(Number(window.__AMAL_DECIMAL_PLACES__)))
    ? Number(window.__AMAL_DECIMAL_PLACES__) : 2;
  if (!amount && amount !== 0) return dp > 0 ? '0.' + '0'.repeat(dp) : '0';
  return Number(amount).toLocaleString('en-PK', {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp
  });
};

const fmtDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-PK', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const ProfitLoss = () => {
  const { activeCompany, activeFinancialYear, activeBranch } = useAuth();
  const navigate = useNavigate();
  const { formatCurrency } = useCurrency();

  // Data state
  const [accounts, setAccounts] = useState([]);
  const [subTransactions, setSubTransactions] = useState([]);
  const [comparisonTransactions, setComparisonTransactions] = useState([]);
  const [financialYears, setFinancialYears] = useState([]);

  // Filter state
  const [selectedFYId, setSelectedFYId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [comparisonMode, setComparisonMode] = useState('none');
  const [showDetails, setShowDetails] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // UI state
  const [loading, setLoading] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});

  // ============================================================
  // LOAD FINANCIAL YEARS
  // ============================================================
  const loadFinancialYears = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const fyRef = collection(db, 'companies', activeCompany.id, 'financialYears');
      const q = query(fyRef, orderBy('startDate', 'desc'));
      const snapshot = await getDocs(q);
      const fys = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setFinancialYears(fys);

      if (activeFinancialYear?.id) {
        setSelectedFYId(activeFinancialYear.id);
        // Set default date range from FY
        if (activeFinancialYear.startDate) setDateFrom(activeFinancialYear.startDate);
        if (activeFinancialYear.endDate) setDateTo(activeFinancialYear.endDate);
      } else if (fys.length > 0) {
        setSelectedFYId(fys[0].id);
        if (fys[0].startDate) setDateFrom(fys[0].startDate);
        if (fys[0].endDate) setDateTo(fys[0].endDate);
      }
    } catch (error) {
      console.error('Error loading financial years:', error);
    }
  }, [activeCompany, activeFinancialYear]);

  // ============================================================
  // LOAD ACCOUNTS
  // ============================================================
  const loadAccounts = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const accountsRef = collection(db, 'companies', activeCompany.id, 'accounts');
      const q = query(accountsRef, orderBy('code', 'asc'));
      const snapshot = await getDocs(q);
      const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAccounts(list);
    } catch (error) {
      console.error('Error loading accounts:', error);
    }
  }, [activeCompany]);

  useEffect(() => {
    loadFinancialYears();
    loadAccounts();
  }, [loadFinancialYears, loadAccounts]);

  // Update dates when FY changes
  useEffect(() => {
    const fy = financialYears.find(f => f.id === selectedFYId);
    if (fy) {
      if (fy.startDate) setDateFrom(fy.startDate);
      if (fy.endDate) setDateTo(fy.endDate);
    }
  }, [selectedFYId, financialYears]);

  // ============================================================
  // CALCULATE COMPARISON DATES
  // ============================================================
  const comparisonDates = useMemo(() => {
    if (comparisonMode === 'none' || !dateFrom || !dateTo) return null;

    const from = new Date(dateFrom);
    const to = new Date(dateTo);
    const daysDiff = Math.round((to - from) / (1000 * 60 * 60 * 24));

    if (comparisonMode === 'previousPeriod') {
      const prevTo = new Date(from);
      prevTo.setDate(prevTo.getDate() - 1);
      const prevFrom = new Date(prevTo);
      prevFrom.setDate(prevFrom.getDate() - daysDiff);
      return {
        from: prevFrom.toISOString().split('T')[0],
        to: prevTo.toISOString().split('T')[0]
      };
    }

    if (comparisonMode === 'previousYear') {
      const prevFrom = new Date(from);
      prevFrom.setFullYear(prevFrom.getFullYear() - 1);
      const prevTo = new Date(to);
      prevTo.setFullYear(prevTo.getFullYear() - 1);
      return {
        from: prevFrom.toISOString().split('T')[0],
        to: prevTo.toISOString().split('T')[0]
      };
    }

    return null;
  }, [comparisonMode, dateFrom, dateTo]);

  // ============================================================
  // GENERATE REPORT
  // ============================================================
  const generateReport = async () => {
    if (!activeCompany?.id || !selectedFYId) {
      alert('Please select a financial year');
      return;
    }

    setLoading(true);
    setHasGenerated(true);

    try {
      // Query current period subTransactions
      const subTransRef = collection(db, 'companies', activeCompany.id, 'subTransactions');
      const conditions = [
        where('status', 'in', ['Posted', 'Draft']),
        where('financialYearId', '==', selectedFYId)
      ];

      const q = query(subTransRef, ...conditions);
      const snapshot = await getDocs(q);
      recordReads(activeCompany.id, snapshot.size);
      let allTrans = snapshot.docs.map(d => ({ id: d.id, ...d.data() })).filter(t => {
        // Include Posted entries always, and Draft entries only from OB (Opening Balance) transactions
        if (t.status === 'Draft' && t.tranTypeCode !== 'OB') return false;
        if (!activeBranch || activeBranch.id === 'all') return true;
        return t.branchId === activeBranch.id;
      });

      // Filter by date range
      if (dateFrom) {
        allTrans = allTrans.filter(t => (t.docDate || '') >= dateFrom);
      }
      if (dateTo) {
        allTrans = allTrans.filter(t => (t.docDate || '') <= dateTo);
      }

      setSubTransactions(allTrans);

      // Query comparison period if needed
      if (comparisonMode !== 'none' && comparisonDates) {
        // For comparison, we query all posted transactions and filter by comparison date range
        const compQ = query(subTransRef, where('status', 'in', ['Posted', 'Draft']));
        const compSnapshot = await getDocs(compQ);
        recordReads(activeCompany.id, compSnapshot.size);
        let compTrans = compSnapshot.docs.map(d => ({ id: d.id, ...d.data() })).filter(t => {
          // Include Posted entries always, and Draft entries only from OB (Opening Balance) transactions
          if (t.status === 'Draft' && t.tranTypeCode !== 'OB') return false;
          if (!activeBranch || activeBranch.id === 'all') return true;
          return t.branchId === activeBranch.id;
        });

        compTrans = compTrans.filter(t => {
          const dd = t.docDate || '';
          return dd >= comparisonDates.from && dd <= comparisonDates.to;
        });

        setComparisonTransactions(compTrans);
      } else {
        setComparisonTransactions([]);
      }

      // Expand all sections by default
      setExpandedSections({
        revenue: true,
        cogs: true,
        grossProfit: true,
        operatingExpenses: true,
        netProfit: true
      });
    } catch (error) {
      console.error('Error generating P&L report:', error);
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // BUILD ACCOUNT HIERARCHY MAPS
  // ============================================================
  const accountMap = useMemo(() => {
    const map = {};
    accounts.forEach(acc => { map[acc.id] = acc; });
    return map;
  }, [accounts]);

  // Get parent chain for an account
  const getParentChain = useCallback((accountId) => {
    const chain = [];
    let current = accountMap[accountId];
    while (current) {
      chain.unshift(current);
      current = current.parentId ? accountMap[current.parentId] : null;
    }
    return chain;
  }, [accountMap]);

  // Get the level 2 (or highest non-root) parent for an account
  const getGroupParent = useCallback((accountId, targetLevel = 2) => {
    const chain = getParentChain(accountId);
    // Find account at the target level
    const parent = chain.find(a => a.level === targetLevel);
    return parent || chain[0];
  }, [getParentChain]);

  // ============================================================
  // COMPUTE P&L DATA
  // ============================================================
  const computePLData = useCallback((transactions) => {
    // Aggregate amounts by accountId
    const accountTotals = {};

    transactions.forEach(t => {
      const accId = t.accountId;
      if (!accId) return;
      const acc = accountMap[accId];
      if (!acc) return;

      const accType = (acc.accountType || '').toLowerCase();
      if (accType !== 'revenue' && accType !== 'expense') return;

      if (!accountTotals[accId]) {
        accountTotals[accId] = {
          accountId: accId,
          code: acc.code || '',
          name: acc.name || '',
          accountType: accType,
          level: acc.level || 5,
          parentId: acc.parentId || null,
          debitTotal: 0,
          creditTotal: 0,
          netAmount: 0
        };
      }
      accountTotals[accId].debitTotal += t.debitAmount || 0;
      accountTotals[accId].creditTotal += t.creditAmount || 0;
    });

    // Calculate net amounts
    Object.values(accountTotals).forEach(at => {
      if (at.accountType === 'revenue') {
        at.netAmount = at.creditTotal - at.debitTotal; // positive = revenue
      } else if (at.accountType === 'expense') {
        at.netAmount = at.debitTotal - at.creditTotal; // positive = expense
      }
    });

    // Separate revenue and expense accounts
    const revenueAccounts = Object.values(accountTotals)
      .filter(a => a.accountType === 'revenue')
      .sort((a, b) => (a.code || '').localeCompare(b.code || ''));

    const expenseAccounts = Object.values(accountTotals)
      .filter(a => a.accountType === 'expense')
      .sort((a, b) => (a.code || '').localeCompare(b.code || ''));

    // COGS: expense accounts with code starting with '51'
    const cogsAccounts = expenseAccounts.filter(a => (a.code || '').startsWith('51'));
    const operatingExpenseAccounts = expenseAccounts.filter(a => !(a.code || '').startsWith('51'));

    // Calculate totals
    const totalRevenue = revenueAccounts.reduce((s, a) => s + a.netAmount, 0);
    const totalCOGS = cogsAccounts.reduce((s, a) => s + a.netAmount, 0);
    const grossProfit = totalRevenue - totalCOGS;
    const totalOperatingExpenses = operatingExpenseAccounts.reduce((s, a) => s + a.netAmount, 0);
    const totalExpenses = expenseAccounts.reduce((s, a) => s + a.netAmount, 0);
    const netProfit = totalRevenue - totalExpenses;
    const profitMargin = totalRevenue !== 0 ? (netProfit / totalRevenue) * 100 : 0;

    // Group accounts by level 2 parent
    const groupAccounts = (accs) => {
      const groups = {};
      accs.forEach(acc => {
        const parent = getGroupParent(acc.accountId, 2);
        const groupKey = parent ? parent.id : 'ungrouped';
        const groupName = parent ? parent.name : 'Other';
        const groupCode = parent ? parent.code : '99';

        if (!groups[groupKey]) {
          groups[groupKey] = {
            id: groupKey,
            name: groupName,
            code: groupCode,
            accounts: [],
            total: 0
          };
        }
        groups[groupKey].accounts.push(acc);
        groups[groupKey].total += acc.netAmount;
      });

      return Object.values(groups).sort((a, b) => (a.code || '').localeCompare(b.code || ''));
    };

    return {
      revenueAccounts,
      expenseAccounts,
      cogsAccounts,
      operatingExpenseAccounts,
      revenueGroups: groupAccounts(revenueAccounts),
      cogsGroups: groupAccounts(cogsAccounts),
      operatingExpenseGroups: groupAccounts(operatingExpenseAccounts),
      totalRevenue,
      totalCOGS,
      grossProfit,
      totalOperatingExpenses,
      totalExpenses,
      netProfit,
      profitMargin
    };
  }, [accountMap, getGroupParent]);

  const currentData = useMemo(() => computePLData(subTransactions), [subTransactions, computePLData]);
  const comparisonData = useMemo(() => computePLData(comparisonTransactions), [comparisonTransactions, computePLData]);

  // ============================================================
  // SEARCH FILTER
  // ============================================================
  const filterBySearch = useCallback((groups) => {
    if (!searchQuery.trim()) return groups;
    const q = searchQuery.toLowerCase();
    return groups.map(group => {
      const filteredAccounts = group.accounts.filter(a =>
        (a.code || '').toLowerCase().includes(q) ||
        (a.name || '').toLowerCase().includes(q)
      );
      if (filteredAccounts.length > 0 || group.name.toLowerCase().includes(q)) {
        return {
          ...group,
          accounts: filteredAccounts.length > 0 ? filteredAccounts : group.accounts
        };
      }
      return null;
    }).filter(Boolean);
  }, [searchQuery]);

  const filteredRevenueGroups = useMemo(() => filterBySearch(currentData.revenueGroups), [currentData.revenueGroups, filterBySearch]);
  const filteredCogsGroups = useMemo(() => filterBySearch(currentData.cogsGroups), [currentData.cogsGroups, filterBySearch]);
  const filteredOpExGroups = useMemo(() => filterBySearch(currentData.operatingExpenseGroups), [currentData.operatingExpenseGroups, filterBySearch]);

  // ============================================================
  // CHART DATA
  // ============================================================
  const revenueVsExpenseData = useMemo(() => {
    if (!hasGenerated) return [];
    const data = [{
      name: 'Current Period',
      Revenue: Math.abs(currentData.totalRevenue),
      Expenses: Math.abs(currentData.totalExpenses)
    }];
    if (comparisonMode !== 'none' && comparisonTransactions.length > 0) {
      data.push({
        name: comparisonMode === 'previousYear' ? 'Previous Year' : 'Previous Period',
        Revenue: Math.abs(comparisonData.totalRevenue),
        Expenses: Math.abs(comparisonData.totalExpenses)
      });
    }
    return data;
  }, [hasGenerated, currentData, comparisonData, comparisonMode, comparisonTransactions]);

  const expenseBreakdownData = useMemo(() => {
    if (!hasGenerated) return [];
    const allExpenseGroups = [
      ...currentData.cogsGroups,
      ...currentData.operatingExpenseGroups
    ];
    return allExpenseGroups
      .filter(g => g.total > 0)
      .map(g => ({
        name: g.name,
        value: Math.abs(g.total)
      }))
      .sort((a, b) => b.value - a.value);
  }, [hasGenerated, currentData]);

  const comparisonBarData = useMemo(() => {
    if (comparisonMode === 'none' || !hasGenerated || comparisonTransactions.length === 0) return [];
    const compLabel = comparisonMode === 'previousYear' ? 'Previous Year' : 'Previous Period';
    return [
      { name: 'Revenue', Current: Math.abs(currentData.totalRevenue), [compLabel]: Math.abs(comparisonData.totalRevenue) },
      { name: 'COGS', Current: Math.abs(currentData.totalCOGS), [compLabel]: Math.abs(comparisonData.totalCOGS) },
      { name: 'Gross Profit', Current: Math.abs(currentData.grossProfit), [compLabel]: Math.abs(comparisonData.grossProfit) },
      { name: 'Op. Expenses', Current: Math.abs(currentData.totalOperatingExpenses), [compLabel]: Math.abs(comparisonData.totalOperatingExpenses) },
      { name: 'Net Profit', Current: currentData.netProfit, [compLabel]: comparisonData.netProfit }
    ];
  }, [comparisonMode, hasGenerated, currentData, comparisonData, comparisonTransactions]);

  // ============================================================
  // SECTION TOGGLE
  // ============================================================
  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const expandAllSections = () => {
    setExpandedSections({
      revenue: true, cogs: true, grossProfit: true,
      operatingExpenses: true, netProfit: true
    });
  };

  const collapseAllSections = () => {
    setExpandedSections({});
  };

  // ============================================================
  // CUSTOM TOOLTIP FOR CHARTS
  // ============================================================
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <TooltipBox>
          <TooltipLabel>{label}</TooltipLabel>
          {payload.map((entry, i) => (
            <TooltipRow key={i}>
              <TooltipDot style={{ background: entry.color }} />
              <span>{entry.name}: {formatCurrency(entry.value)}</span>
            </TooltipRow>
          ))}
        </TooltipBox>
      );
    }
    return null;
  };

  const PieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      const total = expenseBreakdownData.reduce((s, d) => s + d.value, 0);
      const pct = total > 0 ? ((data.value / total) * 100).toFixed(1) : '0.0';
      return (
        <TooltipBox>
          <TooltipLabel>{data.name}</TooltipLabel>
          <TooltipRow>
            <span>{formatCurrency(data.value)} ({pct}%)</span>
          </TooltipRow>
        </TooltipBox>
      );
    }
    return null;
  };

  // ============================================================
  // CSV EXPORT
  // ============================================================
  const handleExport = () => {
    if (!hasGenerated) return;
    const selectedFY = financialYears.find(f => f.id === selectedFYId);
    const hasComparison = comparisonMode !== 'none' && comparisonTransactions.length > 0;

    let csv = 'Profit & Loss Statement\n';
    csv += `Company: ${activeCompany?.companyName || ''}\n`;
    csv += `Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || ''}\n`;
    csv += `Period: ${fmtDate(dateFrom)} to ${fmtDate(dateTo)}\n`;
    if (hasComparison && comparisonDates) {
      csv += `Comparison Period: ${fmtDate(comparisonDates.from)} to ${fmtDate(comparisonDates.to)}\n`;
    }
    csv += '\n';

    if (hasComparison) {
      csv += 'Account Code,Account Name,Current Period,Comparison Period,Change\n';
    } else {
      csv += 'Account Code,Account Name,Amount\n';
    }

    const addLine = (code, name, amount, compAmount) => {
      if (hasComparison) {
        const change = amount - (compAmount || 0);
        csv += `"${code}","${name}",${fmtAmount(amount)},${fmtAmount(compAmount || 0)},${fmtAmount(change)}\n`;
      } else {
        csv += `"${code}","${name}",${fmtAmount(amount)}\n`;
      }
    };

    const addSection = (title, groups, compGroups) => {
      csv += `\n"","${title}",,\n`;
      groups.forEach(group => {
        const compGroup = compGroups ? compGroups.find(g => g.id === group.id) : null;
        group.accounts.forEach(acc => {
          const compAcc = compGroup?.accounts?.find(a => a.accountId === acc.accountId);
          addLine(acc.code, acc.name, acc.netAmount, compAcc?.netAmount);
        });
        addLine('', `Total ${group.name}`, group.total, compGroup?.total);
      });
    };

    addSection('REVENUE', currentData.revenueGroups, comparisonData.revenueGroups);
    addLine('', 'TOTAL REVENUE', currentData.totalRevenue, comparisonData.totalRevenue);

    addSection('COST OF GOODS SOLD', currentData.cogsGroups, comparisonData.cogsGroups);
    addLine('', 'TOTAL COGS', currentData.totalCOGS, comparisonData.totalCOGS);

    csv += '\n';
    addLine('', 'GROSS PROFIT', currentData.grossProfit, comparisonData.grossProfit);

    addSection('OPERATING EXPENSES', currentData.operatingExpenseGroups, comparisonData.operatingExpenseGroups);
    addLine('', 'TOTAL OPERATING EXPENSES', currentData.totalOperatingExpenses, comparisonData.totalOperatingExpenses);

    csv += '\n';
    addLine('', 'NET PROFIT / (LOSS)', currentData.netProfit, comparisonData.netProfit);
    csv += '\n';
    addLine('', 'Profit Margin %', currentData.profitMargin, comparisonData.profitMargin);

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Profit_Loss_${dateFrom}_${dateTo}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // ============================================================
  // PRINT
  // ============================================================
  const handlePrint = () => {
    if (!hasGenerated) return;
    const selectedFY = financialYears.find(f => f.id === selectedFYId);
    const hasComparison = comparisonMode !== 'none' && comparisonTransactions.length > 0;

    const buildSection = (title, groups, compGroups) => {
      let html = '';
      groups.forEach(group => {
        const compGroup = compGroups ? compGroups.find(g => g.id === group.id) : null;
        group.accounts.forEach(acc => {
          const compAcc = compGroup?.accounts?.find(a => a.accountId === acc.accountId);
          html += `<tr class="detail-row">
            <td style="padding-left:40px">${acc.code}</td>
            <td>${acc.name}</td>
            <td class="amount">${fmtAmount(acc.netAmount)}</td>
            ${hasComparison ? `<td class="amount">${fmtAmount(compAcc?.netAmount || 0)}</td>` : ''}
          </tr>`;
        });
        html += `<tr class="subtotal-row">
          <td></td>
          <td>Total ${group.name}</td>
          <td class="amount">${fmtAmount(group.total)}</td>
          ${hasComparison ? `<td class="amount">${fmtAmount(compGroup?.total || 0)}</td>` : ''}
        </tr>`;
      });
      return html;
    };

    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Profit & Loss Statement</title>
      <style>
        body{font-family:'Segoe UI',sans-serif;margin:30px;color:#1A1A1A;font-size:13px}
        .header{background:#800020;color:white;padding:20px;border-radius:8px;margin-bottom:20px}
        .header h1{margin:0;font-size:22px}
        .header .meta{font-size:12px;opacity:0.9;margin-top:6px}
        table{width:100%;border-collapse:collapse;margin-bottom:15px}
        th{background:#f5f5f5;padding:10px;text-align:left;font-size:11px;text-transform:uppercase;border-bottom:2px solid #800020}
        th.amount{text-align:right}
        td{padding:8px 10px;border-bottom:1px solid #eee}
        td.amount{text-align:right;font-family:'Consolas',monospace;font-size:12px}
        .section-header td{font-weight:700;font-size:13px;background:#f9f5f6;color:#800020;border-bottom:2px solid #e0d0d5;padding:12px 10px}
        .subtotal-row td{font-weight:600;border-top:1px solid #ccc}
        .total-row td{font-weight:700;font-size:14px;background:#f0e0e5;border-top:2px solid #800020;border-bottom:2px solid #800020}
        .grand-total td{font-weight:700;font-size:15px;background:#800020;color:white}
        .detail-row td{font-size:12px;color:#444}
        .negative{color:#DC3545}
        .positive{color:#28A745}
        @media print{body{margin:15px}.header{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
      </style></head><body>
      <div class="header">
        <h1>Profit & Loss Statement</h1>
        <div class="meta">
          ${activeCompany?.companyName || 'Company'} |
          Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || ''} |
          Period: ${fmtDate(dateFrom)} to ${fmtDate(dateTo)}
          ${hasComparison && comparisonDates ? ` | Comparison: ${fmtDate(comparisonDates.from)} to ${fmtDate(comparisonDates.to)}` : ''}
        </div>
      </div>
      <table>
        <thead><tr>
          <th style="width:120px">Code</th>
          <th>Particulars</th>
          <th class="amount" style="width:150px">Amount</th>
          ${hasComparison ? '<th class="amount" style="width:150px">Comparison</th>' : ''}
        </tr></thead>
        <tbody>
          <tr class="section-header"><td colspan="${hasComparison ? 4 : 3}">REVENUE</td></tr>
          ${buildSection('Revenue', currentData.revenueGroups, comparisonData.revenueGroups)}
          <tr class="total-row">
            <td></td>
            <td>Total Revenue</td>
            <td class="amount">${fmtAmount(currentData.totalRevenue)}</td>
            ${hasComparison ? `<td class="amount">${fmtAmount(comparisonData.totalRevenue)}</td>` : ''}
          </tr>

          <tr class="section-header"><td colspan="${hasComparison ? 4 : 3}">COST OF GOODS SOLD</td></tr>
          ${buildSection('COGS', currentData.cogsGroups, comparisonData.cogsGroups)}
          <tr class="total-row">
            <td></td>
            <td>Total Cost of Goods Sold</td>
            <td class="amount">${fmtAmount(currentData.totalCOGS)}</td>
            ${hasComparison ? `<td class="amount">${fmtAmount(comparisonData.totalCOGS)}</td>` : ''}
          </tr>

          <tr class="total-row">
            <td></td>
            <td>GROSS PROFIT</td>
            <td class="amount ${currentData.grossProfit >= 0 ? 'positive' : 'negative'}">${fmtAmount(currentData.grossProfit)}</td>
            ${hasComparison ? `<td class="amount ${comparisonData.grossProfit >= 0 ? 'positive' : 'negative'}">${fmtAmount(comparisonData.grossProfit)}</td>` : ''}
          </tr>

          <tr class="section-header"><td colspan="${hasComparison ? 4 : 3}">OPERATING EXPENSES</td></tr>
          ${buildSection('Operating Expenses', currentData.operatingExpenseGroups, comparisonData.operatingExpenseGroups)}
          <tr class="total-row">
            <td></td>
            <td>Total Operating Expenses</td>
            <td class="amount">${fmtAmount(currentData.totalOperatingExpenses)}</td>
            ${hasComparison ? `<td class="amount">${fmtAmount(comparisonData.totalOperatingExpenses)}</td>` : ''}
          </tr>

          <tr class="grand-total">
            <td></td>
            <td>NET PROFIT / (LOSS)</td>
            <td class="amount">${fmtAmount(currentData.netProfit)}</td>
            ${hasComparison ? `<td class="amount">${fmtAmount(comparisonData.netProfit)}</td>` : ''}
          </tr>
        </tbody>
      </table>
    </body></html>`);
    addPrintBrand(w);
    w.document.close();
    w.print();
  };

  // ============================================================
  // RENDER SECTION
  // ============================================================
  const renderAccountSection = (sectionKey, title, groups, totalAmount, compTotalAmount) => {
    const isExpanded = expandedSections[sectionKey];
    const hasComparison = comparisonMode !== 'none' && comparisonTransactions.length > 0;

    return (
      <React.Fragment key={sectionKey}>
        <SectionHeaderRow onClick={() => toggleSection(sectionKey)}>
          <SectionTd colSpan={hasComparison ? 5 : 4}>
            <SectionToggle>
              {isExpanded ? <FiChevronDown /> : <FiChevronRight />}
            </SectionToggle>
            {title}
          </SectionTd>
        </SectionHeaderRow>
        {isExpanded && groups.map(group => (
          <React.Fragment key={group.id}>
            {showDetails && group.accounts.map(acc => {
              const compGroup = hasComparison
                ? (comparisonMode !== 'none' ? getComparisonGroup(sectionKey, group.id) : null)
                : null;
              const compAcc = compGroup?.accounts?.find(a => a.accountId === acc.accountId);

              return (
                <DetailRow key={acc.accountId}>
                  <CodeTd>{acc.code}</CodeTd>
                  <NameTd>
                    <ClickableAccountName
                      onClick={() => navigate('/reports/ledger', {
                        state: { filterAccountId: acc.accountId, filterAccountName: acc.name }
                      })}
                    >
                      {acc.name}
                    </ClickableAccountName>
                  </NameTd>
                  <AmountTd $negative={acc.netAmount < 0}>
                    {formatCurrency(Math.abs(acc.netAmount))}
                  </AmountTd>
                  {hasComparison && (
                    <AmountTd $negative={(compAcc?.netAmount || 0) < 0}>
                      {formatCurrency(Math.abs(compAcc?.netAmount || 0))}
                    </AmountTd>
                  )}
                  {hasComparison && (
                    <ChangeTd $positive={acc.netAmount > (compAcc?.netAmount || 0)} $negative={acc.netAmount < (compAcc?.netAmount || 0)}>
                      {formatCurrency(Math.abs(acc.netAmount - (compAcc?.netAmount || 0)))}
                    </ChangeTd>
                  )}
                </DetailRow>
              );
            })}
            {group.accounts.length > 1 && (
              <SubtotalRow>
                <CodeTd></CodeTd>
                <NameTd>Total {group.name}</NameTd>
                <AmountTd $bold $negative={group.total < 0}>
                  {formatCurrency(Math.abs(group.total))}
                </AmountTd>
                {hasComparison && (() => {
                  const compGroup = getComparisonGroup(sectionKey, group.id);
                  return (
                    <>
                      <AmountTd $bold $negative={(compGroup?.total || 0) < 0}>
                        {formatCurrency(Math.abs(compGroup?.total || 0))}
                      </AmountTd>
                      <ChangeTd $bold $positive={group.total > (compGroup?.total || 0)} $negative={group.total < (compGroup?.total || 0)}>
                        {formatCurrency(Math.abs(group.total - (compGroup?.total || 0)))}
                      </ChangeTd>
                    </>
                  );
                })()}
              </SubtotalRow>
            )}
          </React.Fragment>
        ))}
        <TotalRow>
          <CodeTd></CodeTd>
          <NameTd>Total {title}</NameTd>
          <AmountTd $bold $total $negative={totalAmount < 0}>
            {formatCurrency(Math.abs(totalAmount))}
          </AmountTd>
          {hasComparison && (
            <>
              <AmountTd $bold $total $negative={(compTotalAmount || 0) < 0}>
                {formatCurrency(Math.abs(compTotalAmount || 0))}
              </AmountTd>
              <ChangeTd $bold $total $positive={totalAmount > (compTotalAmount || 0)} $negative={totalAmount < (compTotalAmount || 0)}>
                {formatCurrency(Math.abs(totalAmount - (compTotalAmount || 0)))}
              </ChangeTd>
            </>
          )}
        </TotalRow>
      </React.Fragment>
    );
  };

  // Helper to get comparison group data
  const getComparisonGroup = (sectionKey, groupId) => {
    let groups = [];
    if (sectionKey === 'revenue') groups = comparisonData.revenueGroups;
    else if (sectionKey === 'cogs') groups = comparisonData.cogsGroups;
    else if (sectionKey === 'operatingExpenses') groups = comparisonData.operatingExpenseGroups;
    return groups.find(g => g.id === groupId);
  };

  // ============================================================
  // RENDER
  // ============================================================
  const hasComparison = comparisonMode !== 'none' && comparisonTransactions.length > 0;
  const selectedFY = financialYears.find(f => f.id === selectedFYId);

  return (
    <PageLayout pageTitle="Profit & Loss" breadcrumb="Reports / Accounting Reports / Profit & Loss">
      {/* Summary Cards */}
      {hasGenerated && !loading && (
        <SummaryCards>
          <SummaryCard>
            <SummaryIcon $color="#28A745"><FiTrendingUp /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Total Revenue</SummaryLabel>
              <SummaryValue $color="#28A745">
                {formatCurrency(Math.abs(currentData.totalRevenue))}
              </SummaryValue>
            </SummaryInfo>
          </SummaryCard>
          <SummaryCard>
            <SummaryIcon $color="#DC3545"><FiTrendingDown /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Total Expenses</SummaryLabel>
              <SummaryValue $color="#DC3545">
                {formatCurrency(Math.abs(currentData.totalExpenses))}
              </SummaryValue>
            </SummaryInfo>
          </SummaryCard>
          <SummaryCard>
            <SummaryIcon $color={currentData.netProfit >= 0 ? '#28A745' : '#DC3545'}>
              <FiDollarSign />
            </SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Net {currentData.netProfit >= 0 ? 'Profit' : 'Loss'}</SummaryLabel>
              <SummaryValue $color={currentData.netProfit >= 0 ? '#28A745' : '#DC3545'}>
                {currentData.netProfit < 0 ? '(' : ''}{formatCurrency(Math.abs(currentData.netProfit))}{currentData.netProfit < 0 ? ')' : ''}
              </SummaryValue>
            </SummaryInfo>
          </SummaryCard>
          <SummaryCard>
            <SummaryIcon $color={currentData.profitMargin >= 0 ? '#28A745' : '#DC3545'}>
              <FiPercent />
            </SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Profit Margin</SummaryLabel>
              <SummaryValue $color={currentData.profitMargin >= 0 ? '#28A745' : '#DC3545'}>
                {currentData.profitMargin.toFixed(1)}%
              </SummaryValue>
            </SummaryInfo>
          </SummaryCard>
        </SummaryCards>
      )}

      {/* Filters */}
      <FiltersCard>
        <FiltersRow>
          <FilterGroup>
            <Label>Financial Year</Label>
            <Select value={selectedFYId} onChange={e => setSelectedFYId(e.target.value)}>
              <option value="">Select Financial Year</option>
              {financialYears.map(fy => (
                <option key={fy.id} value={fy.id}>
                  {fy.yearName || fy.yearCode}
                  {fy.isActive ? ' (Active)' : ''}
                </option>
              ))}
            </Select>
          </FilterGroup>
          <FilterGroup>
            <Label>From Date</Label>
            <DateInput type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          </FilterGroup>
          <FilterGroup>
            <Label>To Date</Label>
            <DateInput type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
          </FilterGroup>
          <FilterGroup>
            <Label>Comparison</Label>
            <Select value={comparisonMode} onChange={e => setComparisonMode(e.target.value)}>
              <option value="none">None</option>
              <option value="previousPeriod">Previous Period</option>
              <option value="previousYear">Previous Year</option>
            </Select>
          </FilterGroup>
        </FiltersRow>
        <FiltersRow style={{ marginTop: 14 }}>
          <SearchInputWrap>
            <SearchIcon><FiSearch /></SearchIcon>
            <Input
              type="text"
              placeholder="Search accounts by code or name..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </SearchInputWrap>
          <ToggleButton
            $active={showDetails}
            onClick={() => setShowDetails(!showDetails)}
            title={showDetails ? 'Hide account details' : 'Show account details'}
          >
            {showDetails ? <FiEye /> : <FiEyeOff />}
            {showDetails ? 'Details On' : 'Details Off'}
          </ToggleButton>
          <ButtonGroup>
            <Button onClick={expandAllSections} title="Expand All"><FiChevronDown /> Expand</Button>
            <Button onClick={collapseAllSections} title="Collapse All"><FiChevronRight /> Collapse</Button>
            <Button onClick={handleExport} disabled={!hasGenerated || loading}><FiDownload /> CSV</Button>
            <Button onClick={handlePrint} disabled={!hasGenerated || loading}><FiPrinter /> Print</Button>
            <PrimaryButton onClick={generateReport} disabled={loading}>
              <FiBarChart2 /> {loading ? 'Generating...' : 'Generate Report'}
            </PrimaryButton>
          </ButtonGroup>
        </FiltersRow>
        {comparisonMode !== 'none' && comparisonDates && (
          <ComparisonInfo>
            Comparison period: {fmtDate(comparisonDates.from)} to {fmtDate(comparisonDates.to)}
          </ComparisonInfo>
        )}
      </FiltersCard>

      {/* P&L Table */}
      <ContentCard>
        {hasGenerated && !loading && (
          <ReportHeader>
            <ReportTitle>Income Statement</ReportTitle>
            <ReportMeta>
              {activeCompany?.companyName || ''} | {selectedFY?.yearName || selectedFY?.yearCode || ''} | {fmtDate(dateFrom)} to {fmtDate(dateTo)}
            </ReportMeta>
          </ReportHeader>
        )}

        <TableContainer>
          {loading ? (
            <EmptyState>
              <LoadingSpinner />
              <div style={{ marginTop: 16 }}>Generating Profit & Loss report...</div>
            </EmptyState>
          ) : !hasGenerated ? (
            <EmptyState>
              <FiCalendar size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
              <div>Select a financial year and date range, then click Generate Report</div>
            </EmptyState>
          ) : currentData.totalRevenue === 0 && currentData.totalExpenses === 0 ? (
            <EmptyState>
              <FiBarChart2 size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
              <div>No revenue or expense transactions found for the selected period</div>
              <SubText>Ensure transactions are posted and within the selected date range.</SubText>
            </EmptyState>
          ) : (
            <Table>
              <thead>
                <Tr>
                  <Th style={{ width: 120 }}>Code</Th>
                  <Th>Particulars</Th>
                  <Th $right style={{ width: 160 }}>Amount</Th>
                  {hasComparison && <Th $right style={{ width: 160 }}>Comparison</Th>}
                  {hasComparison && <Th $right style={{ width: 140 }}>Change</Th>}
                </Tr>
              </thead>
              <tbody>
                {/* REVENUE */}
                {renderAccountSection(
                  'revenue', 'Revenue',
                  filteredRevenueGroups,
                  currentData.totalRevenue,
                  comparisonData.totalRevenue
                )}

                {/* SPACER */}
                <SpacerRow><td colSpan={hasComparison ? 5 : 3}></td></SpacerRow>

                {/* COST OF GOODS SOLD */}
                {renderAccountSection(
                  'cogs', 'Cost of Goods Sold',
                  filteredCogsGroups,
                  currentData.totalCOGS,
                  comparisonData.totalCOGS
                )}

                {/* GROSS PROFIT */}
                <GrandTotalRow $highlight>
                  <CodeTd></CodeTd>
                  <NameTd>GROSS PROFIT</NameTd>
                  <AmountTd $bold $grand $negative={currentData.grossProfit < 0}>
                    {currentData.grossProfit < 0 ? '(' : ''}{formatCurrency(Math.abs(currentData.grossProfit))}{currentData.grossProfit < 0 ? ')' : ''}
                  </AmountTd>
                  {hasComparison && (
                    <>
                      <AmountTd $bold $grand $negative={comparisonData.grossProfit < 0}>
                        {comparisonData.grossProfit < 0 ? '(' : ''}{formatCurrency(Math.abs(comparisonData.grossProfit))}{comparisonData.grossProfit < 0 ? ')' : ''}
                      </AmountTd>
                      <ChangeTd $bold $grand
                        $positive={currentData.grossProfit > comparisonData.grossProfit}
                        $negative={currentData.grossProfit < comparisonData.grossProfit}
                      >
                        {formatCurrency(Math.abs(currentData.grossProfit - comparisonData.grossProfit))}
                      </ChangeTd>
                    </>
                  )}
                </GrandTotalRow>

                {/* SPACER */}
                <SpacerRow><td colSpan={hasComparison ? 5 : 3}></td></SpacerRow>

                {/* OPERATING EXPENSES */}
                {renderAccountSection(
                  'operatingExpenses', 'Operating Expenses',
                  filteredOpExGroups,
                  currentData.totalOperatingExpenses,
                  comparisonData.totalOperatingExpenses
                )}

                {/* SPACER */}
                <SpacerRow><td colSpan={hasComparison ? 5 : 3}></td></SpacerRow>

                {/* NET PROFIT / LOSS */}
                <GrandTotalRow $final>
                  <CodeTd></CodeTd>
                  <NameTd>NET PROFIT / (LOSS)</NameTd>
                  <AmountTd $bold $grand $negative={currentData.netProfit < 0}>
                    {currentData.netProfit < 0 ? '(' : ''}{formatCurrency(Math.abs(currentData.netProfit))}{currentData.netProfit < 0 ? ')' : ''}
                  </AmountTd>
                  {hasComparison && (
                    <>
                      <AmountTd $bold $grand $negative={comparisonData.netProfit < 0}>
                        {comparisonData.netProfit < 0 ? '(' : ''}{formatCurrency(Math.abs(comparisonData.netProfit))}{comparisonData.netProfit < 0 ? ')' : ''}
                      </AmountTd>
                      <ChangeTd $bold $grand
                        $positive={currentData.netProfit > comparisonData.netProfit}
                        $negative={currentData.netProfit < comparisonData.netProfit}
                      >
                        {formatCurrency(Math.abs(currentData.netProfit - comparisonData.netProfit))}
                      </ChangeTd>
                    </>
                  )}
                </GrandTotalRow>

                {/* Profit Margin Row */}
                <MarginRow>
                  <CodeTd></CodeTd>
                  <NameTd>Profit Margin</NameTd>
                  <AmountTd $bold $negative={currentData.profitMargin < 0}>
                    {currentData.profitMargin.toFixed(1)}%
                  </AmountTd>
                  {hasComparison && (
                    <>
                      <AmountTd $bold $negative={comparisonData.profitMargin < 0}>
                        {comparisonData.profitMargin.toFixed(1)}%
                      </AmountTd>
                      <ChangeTd $bold
                        $positive={currentData.profitMargin > comparisonData.profitMargin}
                        $negative={currentData.profitMargin < comparisonData.profitMargin}
                      >
                        {Math.abs(currentData.profitMargin - comparisonData.profitMargin).toFixed(1)}%
                      </ChangeTd>
                    </>
                  )}
                </MarginRow>
              </tbody>
            </Table>
          )}
        </TableContainer>
      </ContentCard>

      {/* Charts */}
      {hasGenerated && !loading && (currentData.totalRevenue !== 0 || currentData.totalExpenses !== 0) && (
        <ChartsGrid>
          {/* Revenue vs Expenses Bar Chart */}
          <ChartCard>
            <ChartTitle>Revenue vs Expenses</ChartTitle>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={revenueVsExpenseData} margin={{ top: 10, right: 30, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={v => formatNumber(v, { compact: true })} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Revenue" fill="#28A745" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Expenses" fill="#DC3545" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* Expense Breakdown Pie Chart */}
          <ChartCard>
            <ChartTitle>Expense Breakdown</ChartTitle>
            {expenseBreakdownData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={expenseBreakdownData}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    labelLine={{ stroke: '#999', strokeWidth: 1 }}
                  >
                    {expenseBreakdownData.map((_, i) => (
                      <Cell key={`cell-${i}`} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<PieTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChart>No expense data to display</EmptyChart>
            )}
          </ChartCard>

          {/* Comparison Bar Chart */}
          {hasComparison && comparisonBarData.length > 0 && (
            <ChartCard $full>
              <ChartTitle>
                Current vs {comparisonMode === 'previousYear' ? 'Previous Year' : 'Previous Period'} Comparison
              </ChartTitle>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={comparisonBarData} margin={{ top: 10, right: 30, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} tickFormatter={v => formatNumber(v, { compact: true })} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="Current" fill={CHART_COLORS.primary} radius={[4, 4, 0, 0]} />
                  <Bar
                    dataKey={comparisonMode === 'previousYear' ? 'Previous Year' : 'Previous Period'}
                    fill={CHART_COLORS.accent1}
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          )}
        </ChartsGrid>
      )}
    </PageLayout>
  );
};

// ============================================================
// STYLED COMPONENTS
// ============================================================

const SummaryCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
`;

const SummaryCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SummaryIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${colors.borderRadius.md};
  background: ${props => props.$color || colors.primary.main}15;
  color: ${props => props.$color || colors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  flex-shrink: 0;
`;

const SummaryInfo = styled.div`
  min-width: 0;
`;

const SummaryLabel = styled.div`
  font-size: 12px;
  color: ${colors.text.secondary};
  margin-bottom: 4px;
`;

const SummaryValue = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: ${props => props.$color || colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FiltersCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  margin-bottom: 20px;
`;

const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  align-items: flex-end;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 150px;
`;

const Label = styled.label`
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const Select = styled.select`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  background: white;
  cursor: pointer;
  &:focus { outline: none; border-color: ${colors.primary.main}; }
`;

const DateInput = styled.input`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  &:focus { outline: none; border-color: ${colors.primary.main}; }
`;

const SearchInputWrap = styled.div`
  position: relative;
  flex: 1;
  min-width: 220px;
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${colors.text.secondary};
`;

const Input = styled.input`
  width: 100%;
  padding: 9px 12px 9px 38px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  &:focus { outline: none; border-color: ${colors.primary.main}; }
  &::placeholder { color: ${colors.text.tertiary}; }
`;

const ToggleButton = styled.button`
  padding: 9px 14px;
  border: 1px solid ${props => props.$active ? colors.primary.main : colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  background: ${props => props.$active ? colors.primary.main + '10' : 'white'};
  color: ${props => props.$active ? colors.primary.main : colors.text.primary};
  transition: all 0.2s;
  &:hover { background: ${props => props.$active ? colors.primary.main + '20' : colors.neutral[100]}; }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const Button = styled.button`
  padding: 9px 14px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  background: white;
  color: ${colors.text.primary};
  transition: all 0.2s;
  &:hover { background: ${colors.neutral[100]}; border-color: ${colors.primary.main}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const PrimaryButton = styled.button`
  padding: 9px 18px;
  border: none;
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  background: ${colors.primary.main};
  color: white;
  transition: all 0.2s;
  &:hover { background: ${colors.primary.dark}; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const ComparisonInfo = styled.div`
  margin-top: 12px;
  font-size: 12px;
  color: ${colors.text.secondary};
  background: ${colors.neutral[50]};
  padding: 8px 12px;
  border-radius: ${colors.borderRadius.sm};
  border-left: 3px solid ${colors.primary.main};
`;

// Charts
const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  ${props => props.$full && 'grid-column: 1 / -1;'}
`;

const ChartTitle = styled.h4`
  margin: 0 0 16px 0;
  font-size: 14px;
  font-weight: 600;
  color: ${colors.text.primary};
`;

const EmptyChart = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 280px;
  color: ${colors.text.tertiary};
  font-size: 14px;
`;

// Tooltip
const TooltipBox = styled.div`
  background: white;
  border: 1px solid ${colors.neutral[200]};
  border-radius: ${colors.borderRadius.md};
  box-shadow: ${colors.shadows.md};
  padding: 12px;
  font-size: 12px;
`;

const TooltipLabel = styled.div`
  font-weight: 600;
  margin-bottom: 6px;
  color: ${colors.text.primary};
`;

const TooltipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${colors.text.secondary};
  margin-bottom: 2px;
`;

const TooltipDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
`;

// Content / Table
const ContentCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  overflow: hidden;
`;

const ReportHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid ${colors.neutral[200]};
  background: ${colors.neutral[50]};
`;

const ReportTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: ${colors.primary.main};
`;

const ReportMeta = styled.p`
  margin: 6px 0 0;
  font-size: 13px;
  color: ${colors.text.secondary};
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Tr = styled.tr``;

const Th = styled.th`
  padding: 12px 16px;
  text-align: ${props => props.$right ? 'right' : 'left'};
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${colors.neutral[50]};
  border-bottom: 2px solid ${colors.neutral[200]};
  white-space: nowrap;
`;

// Section header row (clickable)
const SectionHeaderRow = styled.tr`
  cursor: pointer;
  background: ${colors.primary.main}08;
  border-bottom: 2px solid ${colors.primary.main}30;
  &:hover { background: ${colors.primary.main}12; }
`;

const SectionTd = styled.td`
  padding: 14px 16px;
  font-size: 14px;
  font-weight: 700;
  color: ${colors.primary.main};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SectionToggle = styled.span`
  display: flex;
  align-items: center;
  font-size: 16px;
  color: ${colors.primary.main};
`;

// Detail row (individual account)
const DetailRow = styled.tr`
  border-bottom: 1px solid ${colors.neutral[100]};
  &:hover { background: ${colors.neutral[50]}; }
`;

// Subtotal row (group total)
const SubtotalRow = styled.tr`
  border-bottom: 1px solid ${colors.neutral[200]};
  background: ${colors.neutral[50]};
`;

// Section total row
const TotalRow = styled.tr`
  background: ${colors.neutral[100]};
  border-bottom: 1px solid ${colors.neutral[300]};
  border-top: 1px solid ${colors.neutral[300]};
`;

// Grand total rows (Gross Profit, Net Profit)
const GrandTotalRow = styled.tr`
  background: ${props => props.$final ? colors.primary.main : `${colors.primary.main}12`};
  border-top: 2px solid ${colors.primary.main};
  border-bottom: 2px solid ${colors.primary.main};

  td {
    color: ${props => props.$final ? 'white' : colors.primary.main};
    font-size: ${props => props.$final ? '15px' : '14px'};
  }
`;

// Spacer row
const SpacerRow = styled.tr`
  td { padding: 6px; border: none; background: transparent; }
`;

// Margin row
const MarginRow = styled.tr`
  background: ${colors.neutral[50]};
  border-bottom: 1px solid ${colors.neutral[200]};
  td { font-style: italic; }
`;

// Cell styles
const CodeTd = styled.td`
  padding: 10px 16px;
  font-family: 'SF Mono', 'Consolas', 'Courier New', monospace;
  font-size: 12px;
  color: ${colors.text.secondary};
  white-space: nowrap;
`;

const NameTd = styled.td`
  padding: 10px 16px;
  font-size: 13px;
  color: ${colors.text.primary};
`;

const ClickableAccountName = styled.span`
  cursor: pointer;
  color: #1a73e8;
  &:hover {
    text-decoration: underline;
  }
`;

const AmountTd = styled.td`
  padding: 10px 16px;
  text-align: right;
  font-family: 'SF Mono', 'Consolas', 'Courier New', monospace;
  font-size: 13px;
  font-weight: ${props => props.$bold ? 600 : 400};
  color: ${props => {
    if (props.$negative) return '#DC3545';
    if (props.$grand) return 'inherit';
    return colors.text.primary;
  }};
  white-space: nowrap;
`;

const ChangeTd = styled.td`
  padding: 10px 16px;
  text-align: right;
  font-family: 'SF Mono', 'Consolas', 'Courier New', monospace;
  font-size: 12px;
  font-weight: ${props => props.$bold ? 600 : 400};
  color: ${props => {
    if (props.$positive) return '#28A745';
    if (props.$negative) return '#DC3545';
    return colors.text.secondary;
  }};
  white-space: nowrap;
`;

// Loading & Empty states
const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${colors.text.secondary};
  font-size: 14px;
`;

const SubText = styled.p`
  font-size: 13px;
  color: ${colors.text.tertiary};
  margin-top: 8px;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid ${colors.neutral[200]};
  border-top: 3px solid ${colors.primary.main};
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

export default ProfitLoss;
